/**
 * The period levels of the folder trees (owner ask 2026-10-12): under the
 * deepest folder a document is filed in, its date of data becomes ordinary tree
 * levels: Year → Month (a monthly document) or Year → Q1–Q4 (a quarterly one).
 * An annual / YTD / TTM / period-less document sits directly in its year, and a
 * document without a date goes to "Undated", after the years.
 *
 * Read ONLY from the document's own tags as the listing serves them: `dataDate`
 * (the date-of-data tag, `YYYY-MM-DD`, the END of the period the data covers,
 * never the upload date) and `period` (the "Period: …" tag's value, e.g.
 * "Monthly"). Quarters are calendar quarters of that date. Pure and shared, so
 * the admin data room and the buyer's room build the same levels.
 */

export type PeriodNodeKind = 'year' | 'quarter' | 'month' | 'undated' | 'needs_filing';

export interface PeriodNode {
  /** Stable key: `y:2025`, `q:2025-Q1`, `m:2025-03` or `undated`. */
  key: string;
  kind: PeriodNodeKind;
  /** The row's label: "2025", "Q1", "January", "Undated". */
  label: string;
  /** The label with its year: "2025", "Q1 2025", "January 2025", "Undated". */
  fullLabel: string;
  /** Documents at or below this node. */
  count: number;
  children: PeriodNode[];
}

/** The two document fields a period placement reads. */
export interface PeriodFields {
  dataDate?: string | null;
  period?: string | null;
}

export const UNDATED_PERIOD_KEY = 'undated';

/**
 * The row for a folder's documents that are NOT filed there yet — they rest at
 * this category only until their account (or next level) is resolved, and read
 * "Needs filing" (owner 2026-10-12: year rows under "Bank Docs" read as "filed
 * under Bank Docs").
 */
export const NEEDS_FILING_PERIOD_KEY = 'needs-filing';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

function parseDataDate(
  dataDate: string | null | undefined,
): { year: string; month: number | null } | null {
  const m = /^(\d{4})(?:-(\d{2}))?/.exec(dataDate ?? '');
  if (!m?.[1]) return null;
  const month = m[2] ? Number(m[2]) : NaN;
  return { year: m[1], month: month >= 1 && month <= 12 ? month : null };
}

/**
 * The period keys a document sits under, outermost first:
 * `['y:2025', 'm:2025-03']`, `['y:2025', 'q:2025-Q1']`, `['y:2025']` or
 * `['undated']`.
 */
export function periodKeysOf(doc: PeriodFields): string[] {
  const date = parseDataDate(doc.dataDate);
  if (!date) return [UNDATED_PERIOD_KEY];
  const yearKey = `y:${date.year}`;
  const period = (doc.period ?? '').trim().toLowerCase();
  if (date.month !== null && period === 'monthly') {
    return [yearKey, `m:${date.year}-${String(date.month).padStart(2, '0')}`];
  }
  if (date.month !== null && period === 'quarterly') {
    return [yearKey, `q:${date.year}-Q${Math.ceil(date.month / 3)}`];
  }
  return [yearKey];
}

/** Whether a document is in the period node `key` (at or below it). */
export function inPeriod(doc: PeriodFields, key: string): boolean {
  return periodKeysOf(doc).includes(key);
}

/** A period key's kind and labels; null for a malformed key. */
export function describePeriodKey(
  key: string,
): { kind: PeriodNodeKind; label: string; fullLabel: string } | null {
  if (key === UNDATED_PERIOD_KEY)
    return { kind: 'undated', label: 'Undated', fullLabel: 'Undated' };
  // The "Needs filing" row is a period key too: the page reads its key back out
  // of the URL, and a key this function did not know was dropped there — so
  // clicking the row showed the whole folder instead of the documents in it.
  if (key === NEEDS_FILING_PERIOD_KEY)
    return { kind: 'needs_filing', label: 'Needs filing', fullLabel: 'Needs filing' };
  let m = /^y:(\d{4})$/.exec(key);
  if (m) return { kind: 'year', label: m[1]!, fullLabel: m[1]! };
  m = /^q:(\d{4})-Q([1-4])$/.exec(key);
  if (m) return { kind: 'quarter', label: `Q${m[2]}`, fullLabel: `Q${m[2]} ${m[1]}` };
  m = /^m:(\d{4})-(\d{2})$/.exec(key);
  const month = m ? MONTHS[Number(m[2]) - 1] : undefined;
  if (m && month) return { kind: 'month', label: month, fullLabel: `${month} ${m[1]}` };
  return null;
}

/** Years newest first; inside a year, Q1–Q4 then January–December; Undated last. */
function comparePeriodNodes(a: PeriodNode, b: PeriodNode): number {
  const rank: Record<PeriodNodeKind, number> = {
    needs_filing: -1,
    quarter: 0,
    month: 1,
    year: 2,
    undated: 3,
  };
  if (a.kind !== b.kind) return rank[a.kind] - rank[b.kind];
  return a.kind === 'year' ? b.key.localeCompare(a.key) : a.key.localeCompare(b.key);
}

/**
 * The period levels over a set of documents (one folder's own documents), with
 * counts that add up: the nodes' counts sum to `docs.length` when
 * `includeUndated`, and a year's count is its months/quarters plus the
 * documents sitting directly in it.
 */
export function buildPeriodNodes(
  docs: readonly PeriodFields[],
  { includeUndated = true }: { includeUndated?: boolean } = {},
): PeriodNode[] {
  const roots: PeriodNode[] = [];
  const index = new Map<string, PeriodNode>();
  for (const doc of docs) {
    let siblings = roots;
    for (const key of periodKeysOf(doc)) {
      if (key === UNDATED_PERIOD_KEY && !includeUndated) break;
      let node = index.get(key);
      if (!node) {
        const d = describePeriodKey(key);
        if (!d) break;
        node = { key, ...d, count: 0, children: [] };
        index.set(key, node);
        siblings.push(node);
      }
      node.count += 1;
      siblings = node.children;
    }
  }
  const sort = (nodes: PeriodNode[]) => {
    nodes.sort(comparePeriodNodes);
    for (const n of nodes) sort(n.children);
  };
  sort(roots);
  return roots;
}

/**
 * A checklist template's placeholder category ("[Account Name]", "[Bank
 * Account]", "[State]" …): a slot the AI and the questionnaire fill with a real
 * name, not a folder of its own.
 */
/**
 * The account tree's "[Institution]" level (ADR-0037 update 2026-10-15): the
 * template's statement types for banks not yet identified. Shown — only when it
 * holds documents — as "Bank not identified".
 */
export function isInstitutionLevel(name: string | null | undefined): boolean {
  return /^\[\s*institution\s*\]$/i.test((name ?? '').trim());
}

export function isTemplatePlaceholder(name: string | null | undefined): boolean {
  return /^\[[^[\]]+\]$/.test((name ?? '').trim());
}

/**
 * The friendly label for a placeholder that does hold documents:
 * "[Account Name]" → "Unassigned account", "[Bank Account]" → "Unassigned bank
 * account". Any other name is returned unchanged.
 */
export function placeholderDisplayName(name: string): string {
  if (!isTemplatePlaceholder(name)) return name;
  if (isInstitutionLevel(name)) return 'Bank not identified';
  const inner = name
    .trim()
    .slice(1, -1)
    .trim()
    .replace(/\s+name$/i, '')
    .toLowerCase();
  return inner ? `Unassigned ${inner}` : 'Unassigned';
}

/**
 * A document's folder path as text for display: each template placeholder
 * segment reads as its plain name ("Bank Docs / [Institution] / Lines of Credit"
 * is "Bank Docs / Bank not identified / Lines of Credit"), so a raw "[...]" token
 * never reaches a screen. DISPLAY ONLY — the stored path is unchanged.
 */
export function categoryPathLabel(path: readonly string[], separator = ' / '): string {
  return path.map(placeholderDisplayName).join(separator);
}
